import { useMemo } from "react";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import { TimelineSubtaskCard } from "./TimelineTaskComponents";
import {
  BiologyDeepCarousel,
  type DeepBiologySlide,
} from "./EggFreezingBiologyDeepCarouselCard";

/**
 * What a sperm-freezing patient's semen analysis can lead to, one slide per
 * path, while they wait for it.
 *
 * Deliberately not a prediction: every slide says what a clinic usually does
 * with a result of that kind, and none says which kind this patient will get.
 */
export function buildSpermResultPathSlides(
  t: TranslationFunction,
): DeepBiologySlide[] {
  const ns = "homepage.sperm_results_wait.result_paths";
  return [
    {
      id: "enough-to-store",
      visual: "freezing",
      label: t(`${ns}.enough_to_store.if_the_numbers_look_good`),
      title: t(`${ns}.enough_to_store.your_vials_are_stored`),
      body: t(`${ns}.enough_to_store.lab_freezes_the_sample_into_vials`),
      calloutLabel: t(`${ns}.enough_to_store.what_you_get`),
      callout: t(`${ns}.enough_to_store.confirmation_lists_vials_and_fee`),
      detail: {
        title: t(`${ns}.enough_to_store.when_the_sample_is_enough`),
        sections: [
          {
            title: t(`${ns}.enough_to_store.what_enough_means`),
            body: t(`${ns}.enough_to_store.enough_depends_on_future_use`),
          },
          {
            title: t(`${ns}.enough_to_store.what_happens_next`),
            body: t(`${ns}.enough_to_store.keep_the_storage_confirmation`),
          },
        ],
      },
    },
    {
      id: "another-collection",
      visual: "retrieval",
      label: t(`${ns}.another_collection.if_there_are_fewer_vials`),
      title: t(`${ns}.another_collection.another_sample_may_help`),
      body: t(`${ns}.another_collection.clinic_may_suggest_a_second_sample`),
      calloutLabel: t(`${ns}.another_collection.why_it_helps`),
      callout: t(`${ns}.another_collection.more_vials_more_options`),
      detail: {
        title: t(`${ns}.another_collection.banking_another_sample`),
        sections: [
          {
            title: t(`${ns}.another_collection.when_clinics_suggest_it`),
            body: t(`${ns}.another_collection.few_usable_vials_or_iui_later`),
          },
          {
            title: t(`${ns}.another_collection.what_it_involves`),
            body: t(`${ns}.another_collection.same_preparation_as_the_first`),
          },
        ],
      },
    },
    {
      id: "outside-reference-range",
      visual: "quality",
      label: t(`${ns}.outside_reference_range.if_results_are_outside_range`),
      title: t(`${ns}.outside_reference_range.clinic_may_take_a_closer_look`),
      body: t(`${ns}.outside_reference_range.one_analysis_is_a_snapshot`),
      calloutLabel: t(`${ns}.outside_reference_range.worth_knowing`),
      callout: t(`${ns}.outside_reference_range.sperm_take_months_to_develop`),
      detail: {
        title: t(
          `${ns}.outside_reference_range.when_results_are_outside_range`,
        ),
        sections: [
          {
            title: t(`${ns}.outside_reference_range.what_labs_compare_against`),
            body: t(`${ns}.outside_reference_range.who_reference_values`),
          },
          {
            title: t(`${ns}.outside_reference_range.what_may_come_next`),
            body: t(`${ns}.outside_reference_range.repeat_referral_or_reset`),
          },
        ],
      },
    },
  ];
}

export function SpermResultPathsCard({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const slides = useMemo(() => buildSpermResultPathSlides(t), [t]);
  return (
    <TimelineSubtaskCard
      id="sperm-results-wait.result-paths"
      title={t("homepage.sperm_results_wait.what_your_result_could_mean")}
      subtitle={t("homepage.sperm_results_wait.what_usually_happens_next")}
      titleIcon={<OrganicMiniIcon kind="optimization" size={34} />}
      collapsedPreviewHeight={68}
    >
      <BiologyDeepCarousel
        slides={slides}
        onAction={onAction}
        showEstimateBridge={false}
        illustrationVariant="organic-explanatory"
      />
    </TimelineSubtaskCard>
  );
}
